import React, { useState } from "react";
import { HelpCircle, X } from "lucide-react";

/**
 * A small circular "?" button that opens a bottom sheet / modal drawer with
 * page-specific help copy. Pass either an id (keyed into HELP_CONTENT) or a
 * custom `title` + `sections` prop pair.
 *
 * Usage:
 *   <HelpButton id="dashboard" />
 *   <HelpButton title="…" sections={[{heading, text}, ...]} />
 */
export default function HelpButton({ id, title, sections, className = "" }) {
  const [open, setOpen] = useState(false);
  const content = id ? HELP_CONTENT[id] : { title, sections };
  if (!content) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`help-btn-${id || "custom"}`}
        aria-label="Show help"
        className={`nb-btn nb-btn-secondary !py-2 !px-2 !rounded-full ${className}`}
      >
        <HelpCircle size={16} strokeWidth={3} />
      </button>

      {open && (
        <div
          data-testid={`help-sheet-${id || "custom"}`}
          className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-3"
          onClick={() => setOpen(false)}
        >
          <div
            className="nb-card-lg w-full max-w-md max-h-[85vh] overflow-y-auto p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff" }}
          >
            <div className="flex items-center justify-between gap-2 sticky top-0 bg-white pb-2 border-b-2 border-[#1a1a1a]/15">
              <div className="flex items-center gap-2">
                <HelpCircle size={20} strokeWidth={3} />
                <div className="font-display font-black text-xl">{content.title}</div>
              </div>
              <button
                data-testid={`help-close-${id || "custom"}`}
                onClick={() => setOpen(false)}
                aria-label="Close help"
                className="w-9 h-9 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center bg-white hover:bg-[#f4f0eb]"
              >
                <X size={16} strokeWidth={3} />
              </button>
            </div>
            <div className="space-y-3">
              {content.sections.map((s, i) => (
                <div key={i} className="space-y-1">
                  {s.heading && (
                    <div className="font-display font-black text-sm">{s.heading}</div>
                  )}
                  {Array.isArray(s.text) ? (
                    <ul className="text-sm font-medium text-[#1a1a1a] list-disc pl-4 space-y-1">
                      {s.text.map((line, j) => (
                        <li key={j}>{line}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm font-medium text-[#1a1a1a] leading-relaxed">
                      {s.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Central help copy. Each key maps to a page and produces the modal contents.
 * Keep entries short & scannable — help drawers, not tutorials.
 */
export const HELP_CONTENT = {
  dashboard: {
    title: "Dashboard",
    sections: [
      { heading: "What you're seeing", text: "A snapshot of your current bank balance, the last few transactions, and quick links to the parts of SyncBook you use most." },
      { heading: "Switching accounts", text: "Use the account switcher at the top to focus on one bank account. Everything you see below re-scopes to it." },
      { heading: "Quick actions", text: ["+ Add — enter a transaction manually", "📸 Snap — photograph a receipt", "Balance — start a reconcile against your bank", "Register — see every transaction"] },
    ],
  },
  register: {
    title: "Register",
    sections: [
      { heading: "What is it?", text: "Your checkbook register — every transaction in the currently-selected account, sorted newest first." },
      { heading: "Filters", text: "Tap the chips to narrow by type (all/spent/received) or by status (cleared/uncleared). Use the Category dropdown to focus on one category (Food, Bills, Subscriptions, etc.)." },
      { heading: "Category totals", text: "Chips under the filters show spending per category for whatever's currently visible. Tap one to filter the register to just that category." },
      { heading: "Bulk re-categorize", text: "Tap 'Select', then tap rows to select. A bottom bar appears with a category picker + Apply button. Great for cleaning up after a CSV import." },
      { heading: "Cleared vs uncleared", text: "Cleared = the transaction has actually posted at your bank. Uncleared = it's in your register but the bank hasn't shown it yet." },
      { heading: "Editing", text: "Tap any transaction to open the edit form. You can also change which account it belongs to." },
    ],
  },
  reconcile: {
    title: "Reconcile",
    sections: [
      { heading: "What is reconciling?", text: "Making sure your register matches your bank statement. Enter the bank's current balance, then check off the transactions it lists. When they match — you're reconciled." },
      { heading: "How to do it", text: ["1. Enter your current bank balance at the top", "2. Tap each transaction the bank shows as cleared", "3. When the difference reaches $0, hit Save reconcile", "4. History is kept so you can look back later"] },
      { heading: "Difference stuck?", text: "The most common cause is a transaction you forgot to enter. Check your bank for anything not in the register." },
    ],
  },
  budgets: {
    title: "Budgets",
    sections: [
      { heading: "What it does", text: "Set a monthly spending limit per category. As transactions come in, you'll see your progress against each limit." },
      { heading: "Smart Budgets (Pro)", text: "Tap Auto-suggest — SyncBook looks at your last 3 months of spending per category and proposes a limit rounded up to the nearest $5. You can Apply-all or one-by-one." },
      { heading: "Categories", text: "Food, Groceries, Transport, Shopping, Entertainment, Bills, Subscriptions, Gifts, Other. You can create as many rows as you like." },
    ],
  },
  goals: {
    title: "Savings Goals",
    sections: [
      { heading: "What it does", text: "Name a goal (e.g., 'Prom dress'), set a target dollar amount, watch the progress bar fill as you contribute. Optional target date for extra motivation." },
      { heading: "Contributing", text: "In each goal card, type a $ amount and tap Add. Contributions are tracked on the goal — they don't move money between real bank accounts (yet)." },
      { heading: "Achieving", text: "When your current amount reaches the target, the card shows a trophy and 'Nailed it — goal reached!'. Nothing to do — it's automatic." },
      { heading: "Deleting", text: "Tap the trash icon on any goal to remove it. Contributions history is deleted too." },
    ],
  },
  import: {
    title: "CSV Import",
    sections: [
      { heading: "What it does", text: "Upload a bank statement CSV and SyncBook will match each row against your register. New rows can be added, matched rows mark existing transactions as cleared, and duplicates are flagged." },
      { heading: "Target account", text: "Pick which bank account the imported rows should land in. This is important — pick the account that matches the CSV's source." },
      { heading: "Auto-clean duplicates", text: "Leave the checkbox on and SyncBook will scan for duplicates right after the import and offer a one-tap merge." },
    ],
  },
  snap: {
    title: "Snap Receipt",
    sections: [
      { heading: "What it does", text: "Take (or upload) a photo of a paper receipt. GPT-5.2 Vision extracts the amount, payee, date, and category, then hands off to the transaction form for you to review and save." },
      { heading: "Free quota", text: "Free accounts get 20 receipt scans per month. Pro removes the cap. Your usage shows in the amber banner at the top when relevant." },
      { heading: "Best photos", text: "Flat receipt, good lighting, whole receipt in-frame. Blur or glare will hurt accuracy." },
    ],
  },
  recurring: {
    title: "Recurring bills & paychecks",
    sections: [
      { heading: "What it does", text: "Track recurring items — bills you pay every month, paychecks you receive biweekly, scheduled transfers between accounts. The forecast uses these to project the next 2 weeks." },
      { heading: "Posting", text: "When a recurring item's date comes up, hit Post to log the actual transaction. SyncBook rolls the next date forward automatically." },
      { heading: "Account matters", text: "Every recurring item is tied to a specific account. Transfers additionally need a destination account." },
    ],
  },
  duplicates: {
    title: "Duplicates",
    sections: [
      { heading: "What it does", text: "Scans the currently-selected account for transactions that look like duplicates — same amount + type + payee + within 7 days." },
      { heading: "How to clean up", text: ["Each group shows all detected duplicates", "Tap the one you want to KEEP (usually the oldest)", "Hit 'Delete extras' to remove the others", "Uncheck the Fuzzy toggle to see only exact-match duplicates"] },
      { heading: "Not a duplicate?", text: "If a group is a false alarm (same $ + payee but genuinely two different purchases), tap the 'Not a duplicate' button. Those rows are marked so they never re-appear in this scan." },
      { heading: "Nothing dangerous", text: "You always confirm before anything is deleted." },
    ],
  },
  accounts: {
    title: "Accounts",
    sections: [
      { heading: "What it does", text: "Manage every bank account you track in SyncBook — checking, savings, cash, etc." },
      { heading: "Icons on each row", text: ["🔁 Transfer — move money to another account (Pro)", "🧽 Wipe — delete every transaction in this account (keeps the account)", "➡ Move — reassign every transaction to another account", "🗑 Delete — remove this account entirely"] },
      { heading: "Free vs Pro", text: "Free accounts get 1 bank account. Pro's Multi-Account module unlocks unlimited." },
    ],
  },
  profile: {
    title: "Profile",
    sections: [
      { heading: "Text-in transactions", text: "Save your phone number so you can text transactions to the SyncBook Twilio number. Try '-4.50 coffee' or '+150 pay'. Text 'menu' anytime for the full command list." },
      { heading: "SMS default account", text: "Texted-in transactions land in this account unless you add 'acct:name' to the message." },
      { heading: "Weekly Money Health push", text: "Enable browser notifications for a Sunday-evening recap. Free — no SMS or email cost. iOS requires adding SyncBook to your Home Screen first." },
      { heading: "Invite friends → free Pro", text: "Share your unique link. When a friend signs up and logs at least 3 transactions in their first 30 days, you get 30 free days of Pro. Referrals stack — invite 12 friends, you're Pro for a whole year." },
    ],
  },
  admin: {
    title: "Admin",
    sections: [
      { heading: "What it does", text: "Manage every SyncBook user — set their plan, toggle modules, and reset transaction data." },
      { heading: "Modules", text: "Tap a chip to enable/disable a specific feature for that user. Green = enabled. Grey = disabled. Hover for a description." },
      { heading: "Presets", text: "One-tap plans — Free, Starter, Family, SMS Pro, Full Pro. Apply any preset and the modules flip to match." },
      { heading: "Wipe actions", text: ["Wipe all — delete every transaction (keeps accounts)", "Wipe imports — delete only CSV-imported rows"] },
    ],
  },
  forecast: {
    title: "Forecast",
    sections: [
      { heading: "What it does", text: "Projects your balance over the next 2 weeks based on recurring bills, paychecks, and scheduled transfers on the currently-selected account." },
      { heading: "How to make it accurate", text: "Add all your monthly bills and biweekly paychecks to Recurring. The more you enter, the tighter the projection." },
      { heading: "Ups and downs", text: "Dips are usually a bill hitting; jumps are paychecks. Anything unexpected? Check Recurring for a bad date or amount." },
    ],
  },
};
